import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Login from './components/Login';
import ChangePasswordPage from './components/ChangePasswordPage';
import SSOCallback from './components/SSOCallback';
import {
  GuardedUserRoute,
  GuardedAdminRoute,
  GuardedSuperAdminRoute,
  GuardedClientAdminRoute,
  GuardedOnboardRoute,
  GuardedChatRoute,
  GuardedSummaryRoute,
} from './services/authGuard';

import ProjectsLayout from './components/layout/ProjectsLayout';
import PlatformLayout from './components/layout/PlatformLayout';
import ClientLayout from './components/layout/ClientLayout';

import HomePage from './pages/HomePage';
import HomeHubPage from './pages/HomeHubPage';
import ReportsPage from './pages/ReportsPage';
import TeamMembersPage from './pages/TeamMembersPage';
import FrameworkDesignerPage from './pages/framework/FrameworkDesignerPage';
import IntakeFormPage from './pages/intake/IntakeFormPage';
import Gate0IntakePage from './pages/intake/Gate0IntakePage';
import ChatPage from './pages/chat/ChatPage';
import SummaryPage from './pages/summary/SummaryPage';
import Gate1Page from './pages/gate1/Gate1Page';
import TestStrategyPage from './pages/gate1/TestStrategyPage';
import TestCasesPage from './pages/gate1/TestCasesPage';
import UploadTestCasesPage from './pages/gate1/UploadTestCasesPage';
import TestSuitePage from './pages/gate1/TestSuitePage';
import Gate2ExecutionPage from './pages/gate2/Gate2ExecutionPage';
import Gate2ExecutionRunsPage from './pages/gate2/Gate2ExecutionRunsPage';
import Gate3EntryCheckPage from './pages/gate3/Gate3EntryCheckPage';
import Gate1ReadinessPage from './pages/readiness/Gate1ReadinessPage';
import ScrollToTop from './components/ScrollToTop';

import TenantManagementPage from './pages/platform/TenantManagementPage';
import TenantOnboardingPage from './pages/platform/TenantOnboardingPage';
import TenantDetailPage from './pages/platform/TenantDetailPage';
import TenantTemplateConfigPage from './pages/platform/TenantTemplateConfigPage';
import PlatformAnalyticsPage from './pages/platform/PlatformAnalyticsPage';
import AuditLogPage from './pages/platform/AuditLogPage';
import LicenceKeysPage from './pages/platform/LicenceKeysPage';
import PlatformSettingsPage from './pages/platform/PlatformSettingsPage';
import NotificationsPage from './pages/platform/NotificationsPage';

import ClientOverviewPage from './pages/client/ClientOverviewPage';
import ClientUserManagementPage from './pages/client/ClientUserManagementPage';
import ClientRolesPage from './pages/client/ClientRolesPage';
import ClientActivityPage from './pages/client/ClientActivityPage';
import ClientSettingsPage from './pages/client/ClientSettingsPage';

function App() {
  return (
    <AuthProvider>
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/sso-callback" element={<SSOCallback />} />

        <Route element={<GuardedUserRoute />}>
          <Route element={<ProjectsLayout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/home-hub" element={<HomeHubPage />} />
            <Route path="/projects" element={<HomePage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/gate1/:projectId" element={<Gate1Page />} />
            <Route path="/test-strategy/:projectId" element={<TestStrategyPage />} />
            <Route path="/test-cases/:projectId" element={<TestCasesPage />} />
            <Route path="/test-cases/:projectId/upload" element={<UploadTestCasesPage />} />
            <Route path="/test-suites/:projectId" element={<TestSuitePage />} />
            <Route path="/gate2/:projectId" element={<Gate2ExecutionPage />} />
            <Route path="/gate3/:projectId" element={<Gate3EntryCheckPage />} />
            <Route path="/gate2/:projectId/runs" element={<Gate2ExecutionRunsPage />} />
            <Route path="/readiness/:projectId" element={<Gate1ReadinessPage />} />
          </Route>
        </Route>

        <Route element={<GuardedOnboardRoute />}>
          <Route element={<ProjectsLayout />}>
            <Route path="/intake/gate0" element={<Gate0IntakePage />} />
            <Route path="/framework/:projectId" element={<FrameworkDesignerPage />} />
            <Route
              path="/framework/:projectId/customize-template"
              element={<TenantTemplateConfigPage />}
            />
            <Route path="/intake/:projectId" element={<IntakeFormPage />} />
          </Route>
        </Route>

        <Route element={<GuardedChatRoute />}>
          <Route element={<ProjectsLayout />}>
            <Route path="/chat/:projectId" element={<ChatPage />} />
          </Route>
        </Route>

        <Route element={<GuardedSummaryRoute />}>
          <Route element={<ProjectsLayout />}>
            <Route path="/summary/:projectId" element={<SummaryPage />} />
          </Route>
        </Route>

        <Route element={<GuardedAdminRoute />}>
          <Route element={<ProjectsLayout />}>
            <Route path="/team-members" element={<TeamMembersPage />} />
          </Route>
        </Route>

        <Route element={<GuardedSuperAdminRoute />}>
          <Route element={<PlatformLayout />}>
            <Route path="/platform/tenants" element={<TenantManagementPage />} />
            <Route path="/platform/tenants/new" element={<TenantOnboardingPage />} />
            <Route path="/platform/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/platform/analytics" element={<PlatformAnalyticsPage />} />
            <Route path="/platform/audit" element={<AuditLogPage />} />
            <Route path="/platform/licence-keys" element={<LicenceKeysPage />} />
            <Route path="/platform/settings" element={<PlatformSettingsPage />} />
            <Route path="/platform/notifications" element={<NotificationsPage />} />
          </Route>
        </Route>

        <Route element={<GuardedClientAdminRoute />}>
          <Route element={<ClientLayout />}>
            <Route path="/client/overview" element={<ClientOverviewPage />} />
            <Route path="/client/users" element={<ClientUserManagementPage />} />
            <Route path="/client/roles" element={<ClientRolesPage />} />
            <Route path="/client/activity" element={<ClientActivityPage />} />
            <Route path="/client/settings" element={<ClientSettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </AuthProvider>
  );
}

export default App;
